import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import SelectInput from "../../components/forms/SelectInput";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";

export default function BuyerLot() {
  const saved = useStore().state.buyer.current.preferences.lot || {};
  const [form, setForm] = useState({
    lotType: saved.lotType || [],
    fencing: saved.fencing || [],
    yardSize: saved.yardSize || "",
    landscaping: saved.landscaping || "",
    driveway: saved.driveway || [],
    rvParking: saved.rvParking || "",
    garage: saved.garage || "",
    hoa: saved.hoa || "",
    terrainDetails: saved.terrainDetails || [],
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  return (
    <ChecklistStep
      title="Lot & land"
      lead="The lot is half the home. Capture how you want to park, play, garden, and live outside."
      preferenceKey="lot"
      value={form}
    >
      <section className="checklist-section">
        <h2>Lot Type</h2>
        <ChoiceGroup
          label="Lot position"
          name="lotType"
          multiple
          values={form.lotType}
          onChange={update}
          options={[
            { value: "corner", label: "Corner" },
            { value: "cul-de-sac", label: "Cul-de-sac" },
            { value: "interior", label: "Interior lot" },
          ]}
        />
        <SelectInput
          label="Yard size"
          name="yardSize"
          value={form.yardSize}
          onChange={update}
          options={[
            { value: "none", label: "Little to no yard" },
            { value: "small", label: "Small / low-maintenance" },
            { value: "medium", label: "Typical backyard" },
            { value: "large", label: "Large yard" },
            { value: "acreage", label: "Acreage" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Fencing & Landscaping</h2>
        <CheckboxGroup
          label="Fencing type"
          name="fencing"
          values={form.fencing}
          onChange={update}
          layout="grid"
          options={[
            { value: "none", label: "No fence needed" },
            { value: "wood", label: "Wood privacy" },
            { value: "vinyl", label: "Vinyl" },
            { value: "iron", label: "Iron / view fence" },
            { value: "block", label: "Block / masonry" },
            { value: "open", label: "Open to adding a fence" },
          ]}
        />
        <ChoiceGroup
          label="Landscaping preference"
          name="landscaping"
          value={form.landscaping}
          onChange={update}
          options={[
            { value: "low", label: "Low-maintenance" },
            { value: "established", label: "Established gardens" },
            { value: "blank", label: "Blank slate" },
            { value: "drought", label: "Drought-tolerant / xeriscape" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Driveway, Parking & Garage</h2>
        <ChoiceGroup
          label="Driveway type"
          name="driveway"
          multiple
          values={form.driveway}
          onChange={update}
          options={[
            { value: "straight", label: "Straight" },
            { value: "curved", label: "Curved" },
            { value: "long", label: "Long" },
            { value: "short", label: "Short" },
          ]}
        />
        <ChoiceGroup
          label="RV / boat / extra parking"
          name="rvParking"
          value={form.rvParking}
          onChange={update}
          options={[
            { value: "required", label: "Required" },
            { value: "nice", label: "Nice to have" },
            { value: "no", label: "Not needed" },
          ]}
        />
        <SelectInput
          label="Garage size"
          name="garage"
          value={form.garage}
          onChange={update}
          options={[
            { value: "none", label: "No garage is okay" },
            { value: "1-car", label: "1-car" },
            { value: "2-car", label: "2-car" },
            { value: "3-car", label: "3-car+" },
            { value: "detached", label: "Detached is fine" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>HOA & Terrain Details</h2>
        <ChoiceGroup
          label="HOA preference"
          name="hoa"
          value={form.hoa}
          onChange={update}
          options={[
            { value: "yes", label: "HOA is okay / preferred" },
            { value: "no", label: "No HOA" },
            { value: "either", label: "Either is fine" },
          ]}
        />
        <CheckboxGroup
          label="Terrain details"
          name="terrainDetails"
          values={form.terrainDetails}
          onChange={update}
          layout="grid"
          options={[
            { value: "level", label: "Level / usable" },
            { value: "slope", label: "Gentle slope is okay" },
            { value: "steep", label: "Steep lots okay" },
            { value: "views", label: "View lot" },
            { value: "privacy", label: "Privacy from neighbors" },
            { value: "drainage", label: "Strong drainage important" },
            { value: "flood", label: "Avoid flood-prone lots" },
            { value: "trees", label: "Mature trees" },
          ]}
        />
        <NotesField
          label="Lot notes for my agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Pets and fencing, ADU setbacks, septic vs. sewer, well water, or anything about the land."
        />
      </section>
    </ChecklistStep>
  );
}
